import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import { selectAudiencesPagination } from '@mm/state/selectors/messengerAdSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AudienceType } from '../model/AudiencesModel';

export const useFetchAudiences = (options?: {
  skipBusy?: boolean;
  withoutSystemFilters?: boolean;
  fitchFirstPageOnInit?: boolean;
  fetchAllContactsOnInit?: boolean;
  withoutfetchMore?: boolean;
  inUse?: boolean;
}) => {
  const {
    skipBusy,
    withoutSystemFilters,
    fitchFirstPageOnInit,
    fetchAllContactsOnInit,
    withoutfetchMore,
    inUse,
  } = options || {};
  const audiencesCRUDActions = useAudiencesCRUDActions();
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const pagination = useSelector(selectAudiencesPagination);

  const [isFetching, setIsFetching] = useState(false);
  const fetchState = useRef<{
    busy: boolean;
    nextPage: number;
    pageSize: number;
  }>({
    busy: false,
    nextPage: !pagination?.isLastPage ? pagination.page + 1 : 0,
    pageSize: pagination.perPage,
  });

  const fetchAllContacts = useCallback(() => {
    return (async () => {
      const res = await audiencesCRUDActions.retrieveAll(
        activeChatbotId,
        1,
        1,
        'All contacts',
        true,
        { skipUpdatingReduxPagination: true, inUse },
      );
      return res?.audiences[0];
    })();
  }, [activeChatbotId, audiencesCRUDActions]);

  const fetch = useCallback(
    (
      pageNumber: number,
      pageSize: number,
      query = '',
      options: {
        sortColumn?: 'created_at' | 'contacts_count';
        sortDirection?: 'asc' | 'desc';
        inUse?: boolean;
        type?: AudienceType;
        skipUpdatingReduxPagination?: boolean;
      } = {},
      mergeResults?: boolean,
    ) => {
      return (async () => {
        if (!fetchState.current.busy) {
          fetchState.current.busy = true;
          setIsFetching(true);
          const res = await audiencesCRUDActions.retrieveAll(
            activeChatbotId,
            pageNumber,
            pageSize,
            query,
            mergeResults,
            {
              withoutSystemFilters,
              sortColumn: options?.sortColumn,
              sortDirection: options.sortDirection,
              inUse: options.inUse,
              type: options.type,
              skipUpdatingReduxPagination: options.skipUpdatingReduxPagination,
            },
          );
          setIsFetching(false);
          fetchState.current = {
            busy: false,
            pageSize,
            nextPage:
              res?.pagination.isLastPage === false
                ? res.pagination.page + 1
                : 0,
          };
          return res;
        }
      })();
    },
    [activeChatbotId, audiencesCRUDActions],
  );

  const fetchMore = useCallback(async () => {
    if (!fetchState.current.busy && fetchState.current.nextPage) {
      fetchState.current.busy = true;
      setIsFetching(true);
      const res = await audiencesCRUDActions.retrieveAll(
        activeChatbotId,
        fetchState.current.nextPage,
        fetchState.current.pageSize,
        '',
        true,
        { skipBusy, withoutSystemFilters },
      );
      setIsFetching(false);
      fetchState.current.busy = false;
      fetchState.current.nextPage =
        res?.pagination.isLastPage === false ? res.pagination.page + 1 : 0;
      return res;
    }
  }, [audiencesCRUDActions, activeChatbotId, skipBusy]);

  useEffect(() => {
    (async () => {
      if (fitchFirstPageOnInit) {
        await fetch(1, 10, '', { inUse });
      }
      if (fetchAllContactsOnInit) {
        fetchAllContacts();
      }
      if (!withoutfetchMore) {
        await fetchMore();
      }
    })();
  }, [activeChatbotId, fetchMore]);

  return { fetchAllContacts, fetch, fetchMore, isFetching, pagination };
};
